import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceAnchorType,
  getSubTaskTemplates,
  IExperience,
  IExperienceTemplate,
  ISubTask,
  ITaskTemplate,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import {
  PAEventType,
  ProductAnalyticsEvent
} from '@dadohr/shared/domainTypes/productAnalytics';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { flatten, groupBy, orderBy, uniq } from 'lodash';
import { ExternalLink } from 'lucide-react';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import {
  ItemSorter,
  RowsRenderer,
  ROW_HEIGHTS,
  Sorters
} from '../../../../components/GroupableList';
import { SubTaskAsText } from '../../../../components/SubTask';
import { SubTaskIcon } from '../../../../components/SubTaskIcon';
import { IColumn } from '../../../../components/Table/VirtualizedSortable';
import { SortDirection, useSort } from '../../../../hooks/useSort';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useCurrentUser } from '../../../../services/currentUser';
import {
  getAllActorUserIds,
  getTaskByTaskTemplateId,
  isExperienceUserActor
} from '../../../../services/experiences/instance';
import { useMappedLoadingValue } from '../../../../services/firebase';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { toMoment } from '../../../../services/time';
import { toFullName, useUserMapById } from '../../../../services/user';
import { SubTaskStatusChip } from '../../pages/Details/ParticipantDialogComponents/Task';
import { AnchorDateDiffWarning } from '../ParticipantsTable';
import { TaskStatusChip } from '../TaskStatusChip';
import { GroupDivider } from './components/GroupDivider';
import { NotApplicable } from './components/NotApplicable';
import { TimeZone } from './components/Timezone';

export const OnHover = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: p.theme.spacing(0.5),
  alignItems: 'center',
  '.showOnHover': {
    display: 'flex',
    alignItems: 'center',
    visibility: 'hidden'
  },
  ':hover': {
    color: p.theme.custom.colors.primary.main,
    cursor: 'pointer',

    '.showOnHover': {
      visibility: 'visible'
    }
  }
}));

export const SubItemContainer = styled('div')((p) => ({
  padding: p.theme.spacing(1),
  marginLeft: 9
}));

const SubTaskItem = styled('div')((p) => ({
  display: 'flex',
  gap: 8,
  margin: p.theme.spacing(1)
}));

type ColumnName =
  | 'participant'
  | 'firstDayAtWork'
  | 'availableFrom'
  | 'completedOn'
  // | 'department' Commenting out for now since not part of ticket scope, but might be used in a later point in time
  | 'supportingActorName';

type D = {
  exp: Doc<IExperience>;
  actors: { [actorKey: string]: Doc<IUser> | null };
};

type Group = {
  key: string;
  rows: D[];
};

type OtherProps = {
  tpl: Doc<IExperienceTemplate>;
  taskTplId: string;
  actor: { key: string; label: string };
  onShowInstanceDetails: (d: Doc<IExperience>) => void;
  trackEvent: (event: ProductAnalyticsEvent) => Promise<void>;
};

export const formatTaskToExpandableComponent = (
  a: D[],
  taskTpl: ITaskTemplate
) => {
  return new Map(
    a.map((row) => {
      const task = row.exp.data.tasks.find(
        (task) => task.templateId === taskTpl.id
      );
      return [
        row.exp.id,
        <ExpandableTask taskTpl={taskTpl} subTasks={task?.subTasks || []} />
      ];
    })
  );
};

const withActorOrNull = <T extends unknown>(
  d: D,
  actor: string,
  fn: (user: Doc<IUser>) => T
): T | null => {
  const a = d.actors[actor];
  return a ? fn(a) : null;
};

const withParticipantOrNull = <T extends unknown>(
  d: D,
  fn: (user: Doc<IUser>) => T
): T | null => withActorOrNull(d, PARTICIPANT_ACTOR_KEY, fn);

type Column = IColumn<D, ColumnName, OtherProps>;

const COMMON_COLUMNS: Column[] = [
  {
    key: 'firstDayAtWork',
    head: () => 'First day at work',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        if (
          o.tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE &&
          o.tpl.data.anchor.field === 'hireDate'
        ) {
          const originalDate =
            u.data.hireDate && toMoment(u.data.hireDate).tz(o.tpl.data.tz);
          // This is a rare exception for broken instances which could NOT be migrated when this field was introduced
          if (!d.exp.data.anchorDate?.at) {
            return (
              <FlexContainer>
                <div>{originalDate?.format(org.dateFormats.MMDDYYYY)}</div>
              </FlexContainer>
            );
          }
          const currentDate = toMoment(d.exp.data.anchorDate.at).tz(
            o.tpl.data.tz
          );
          return (
            <FlexContainer>
              <div>{currentDate.format(org.dateFormats.MMDDYYYY)}</div>
              <AnchorDateDiffWarning
                original={originalDate}
                current={currentDate}
              />
            </FlexContainer>
          );
        }
        return u.data.hireDate
          ? toMoment(u.data.hireDate).format(org.dateFormats.MMDDYYYY)
          : '';
      }),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },

  {
    key: 'availableFrom',
    head: () => 'Available from',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        const time = d.exp.data.tasks.find(
          (task) => task.templateId === o.taskTplId
        )?.timing.start;
        const { t } = useUnsafeTranslation();
        if (!time) {
          return <NotApplicable>{t('N/A')}</NotApplicable>;
        }
        return (
          <FlexContainer>
            {toMoment(time)
              .tz(o.tpl.data.tz)
              .format(`${org.dateFormats.MMDDYYYY} HH:mm`)}
            <TimeZone> {toMoment(time).tz(o.tpl.data.tz).format('z')}</TimeZone>
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },

  {
    key: 'completedOn',
    head: () => 'Completed on',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const { t } = useUnsafeTranslation();
        const currentUser = useCurrentUser();
        const { org } = currentUser;
        const task = d.exp.data.tasks.find(
          (task) => task.templateId === o.taskTplId
        );
        if (task && task.status === 'COMPLETE') {
          const completedAt = task.completedAt;
          if (!completedAt) {
            console.warn('Data incomplete!', { completedAt });
            return <NotApplicable>{t('N/A')}</NotApplicable>;
          }
          return (
            <FlexContainer>
              {toMoment(completedAt)
                .tz(o.tpl.data.tz)
                .format(`${org.dateFormats.MMDDYYYY} HH:mm`)}
              <TimeZone>
                {' '}
                {toMoment(completedAt).tz(o.tpl.data.tz).format('z')}
              </TimeZone>
            </FlexContainer>
          );
        }
        return <NotApplicable>{t('N/A')}</NotApplicable>;
      }),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  }

  //Commenting out for now since not part of ticket scope, but might be used in a later point in time

  // {
  //   key: 'department',
  //   head: () => 'Department',
  //   cell: (d) => withParticipantOrNull(d, (u) => u.data.department),
  //   align: 'left',
  //   sortable: true,
  //   defaultDirection: 'asc',
  //   width: 150,
  //   flexGrow: 1
  // }
];

const PARTICIPANT_COLUMNS: Column[] = [
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d, o) =>
      withParticipantOrNull(d, (u) => {
        const task = d.exp.data.tasks.find(
          (task) => task.templateId === o.taskTplId
        );
        return (
          <FlexContainer>
            {task && <TaskStatusChip autoWidth status={task.status} />}
            <Participant
              participant={
                toFullName(u.data) || u.data.email || u.data.personalEmail
              }
              onClick={() => {
                o.onShowInstanceDetails(d.exp);
                o.trackEvent({
                  eventType: PAEventType.EXPERIENCE_INSTANCE,
                  name: 'Admin Instance View Loaded',
                  'Instance ID': d.exp.id,
                  Trigger: 'Timeline Item People Tab',
                  Explanation:
                    'Admin user views the details of a single instance'
                });
              }}
            />
          </FlexContainer>
        );
      }),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  ...COMMON_COLUMNS
];

const SUPPORTING_ACTOR_COLUMNS: Column[] = [
  {
    key: 'supportingActorName',
    head: (o) => o?.actor.label,
    cell: (d, o) => {
      return withActorOrNull(d, o.actor.key, (u) => {
        const task = d.exp.data.tasks.find(
          (task) => task.templateId === o.taskTplId
        );
        return (
          <FlexContainer>
            {task && <TaskStatusChip autoWidth status={task.status} />}
            <Participant
              participant={
                toFullName(u.data) || u.data.email || u.data.personalEmail
              }
              onClick={() => {
                o.onShowInstanceDetails(d.exp);
                o.trackEvent({
                  eventType: PAEventType.EXPERIENCE_INSTANCE,
                  name: 'Admin Instance View Loaded',
                  'Instance ID': d.exp.id,
                  Trigger: 'Timeline Item People Tab',
                  Explanation:
                    'Admin user views the details of a single instance'
                });
              }}
            />
          </FlexContainer>
        );
      });
    },
    align: 'left',
    sortable: true,
    width: 150,
    flexGrow: 1
  },
  {
    key: 'participant',
    head: () => 'Participant',
    cell: (d, o) =>
      withParticipantOrNull(
        d,
        (u) => toFullName(u.data) || u.data.email || u.data.personalEmail
      ),
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  ...COMMON_COLUMNS
];

type GroupSummary = {
  count: number;
};

const SORTERS: Sorters<GroupSummary, D, OtherProps> = {
  firstDayAtWork: {
    key: 'firstDayAtWork',
    label: 'First Day At Work',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      sort: (d) =>
        withParticipantOrNull(d, (u) => u.data.hireDate?.toMillis()) ||
        -Infinity,
      dir: 'asc'
    }
  },

  availableFrom: {
    key: 'availableFrom',
    label: 'Available from',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      sort: (d, o) =>
        getTaskByTaskTemplateId(d.exp, o.taskTplId)?.timing.start.toMillis() ||
        -Infinity,
      dir: 'desc'
    }
  },

  completedOn: {
    key: 'completedOn',
    label: 'Completed on',
    groups: { sort: (g) => g.key, dir: 'asc' },
    items: {
      sort: (d, o) =>
        getTaskByTaskTemplateId(d.exp, o.taskTplId)?.completedAt?.toMillis() ||
        -Infinity,
      dir: 'asc'
    }
  }
};

const ROW_TO_KEY = (d: D) => d.exp.id;
const DEFAULT_SORTER = SORTERS.availableFrom;

export const TasksExecutionReportTable = ({
  instances,
  taskTplId,
  taskTplTitle,
  tpl,
  taskTpl,
  actor,
  onShowInstanceDetails,
  rowToHrefExternal
}: {
  instances: Doc<IExperience>[];
  taskTplId: string;
  taskTplTitle: string;
  tpl: Doc<IExperienceTemplate>;
  taskTpl: ITaskTemplate;
  actor: { key: string; label: string };
  onShowInstanceDetails: (d: Doc<IExperience>) => void;
  rowToHrefExternal?: (d: Doc<IExperience>) => string;
}) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  const { sortBy, direction, setSort } = useSort<ItemSorter<D, OtherProps>>(
    DEFAULT_SORTER,
    DEFAULT_SORTER.items.dir
  );

  const currentUser = useCurrentUser();
  const { org } = currentUser;

  const weNeedGrouping = sortBy.key === 'availableFrom';

  const userIds = useMemo(
    () => uniq(flatten(instances.map((d) => getAllActorUserIds(d)))),
    [instances]
  );

  const toRows = useCallback(
    (userMap: { [key: string]: Doc<IUser> }) =>
      instances.map<D>((exp) => {
        const actors: { [actorKey: string]: Doc<IUser> } = {};
        exp.data.actors.filter(isExperienceUserActor).forEach((a) => {
          actors[a.key] = userMap[a.userId] || null;
        });
        return {
          exp,
          actors: actors
        };
      }),
    [instances]
  );

  const [rows] = useMappedLoadingValue(
    useUserMapById(tpl.data.orgId, userIds),
    toRows,
    true
  );

  const otherProps: OtherProps = useMemo(
    () => ({ tpl, taskTplId, actor, onShowInstanceDetails, trackEvent }),
    [taskTplId, actor, tpl, onShowInstanceDetails, trackEvent]
  );

  const groups = useMemo(() => {
    if (!rows) return [];
    const grouped = groupBy(rows, (r) => {
      return moment(
        r.exp.data.tasks
          .find((task) => task.templateId === taskTplId)
          ?.timing.start.toMillis()
      )
        .startOf('d')
        .valueOf();
    });
    return orderBy(
      Object.keys(grouped).map((group) => ({
        key: group,
        rows: grouped[group]
      })),
      (s) => parseInt(s.key),
      direction
    );
  }, [rows, taskTplId, direction]);

  const columns =
    actor.key === PARTICIPANT_ACTOR_KEY
      ? PARTICIPANT_COLUMNS
      : SUPPORTING_ACTOR_COLUMNS;

  if (!rows) {
    return null;
  }

  const rowsWithTasks = formatTaskToExpandableComponent(rows, taskTpl);

  if (weNeedGrouping) {
    return (
      <GroupedTaskTable
        groups={groups}
        renderGroupDivider={(group) => (
          <div>
            {t('Available from')}:{' '}
            {moment(parseInt(group.key)).format(org.dateFormats.MMDDYYYY)}
          </div>
        )}
        columns={columns}
        sortBy={sortBy}
        direction={direction}
        otherProps={otherProps}
        setSort={setSort}
        sidePadding
        expandableElements={rowsWithTasks}
        onExpansionClick={(rowId: string) =>
          trackEvent({
            eventType: PAEventType.EXPERIENCE_TPL,
            name: 'Timeline Item Modal People Tab Item Expanded',
            Explanation:
              'Admin user expands a row in the People Tab, to see the details of the timeline item in one instance',
            'Timeline Item ID': taskTplId,
            'Experience Template ID': tpl.id,
            'Timeline Item Title': taskTplTitle,
            'Timeline Item Type': 'TASK',
            'Status of Expanded Item':
              rows
                .find((el) => el.exp.id === rowId)
                ?.exp.data.tasks.find((el) => el.templateId === taskTplId)
                ?.status || 'NOT FOUND'
          })
        }
      />
    );
  }

  return (
    <RowsRenderer
      variant="contained"
      columns={columns}
      rows={rows}
      rowToKey={ROW_TO_KEY}
      sorter={sortBy}
      sortDirection={direction}
      renderHead={true}
      headProps={{ sticky: true }}
      onHeadClick={(c, dir) => {
        const sorter = SORTERS[c.key];
        if (!sorter) {
          return;
        }
        setSort(sorter, dir || sorter.items.dir);
      }}
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
      expandableElements={rowsWithTasks}
      sidePadding
      onExpansionClick={(rowId: string) =>
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Timeline Item Modal People Tab Item Expanded',
          Explanation:
            'Admin user expands a row in the People Tab, to see the details of the timeline item in one instance',
          'Timeline Item ID': taskTplId,
          'Experience Template ID': tpl.id,
          'Timeline Item Title': taskTplTitle,
          'Timeline Item Type': 'TASK',
          'Status of Expanded Item':
            rows
              .find((el) => el.exp.id === rowId)
              ?.exp.data.tasks.find((el) => el.templateId === taskTplId)
              ?.status || 'NOT FOUND'
        })
      }
    />
  );
};

const GroupedTaskTable = ({
  groups,
  renderGroupDivider,
  columns,
  sortBy,
  direction,
  otherProps,
  setSort,
  _onRowClick,
  _rowToHrefExternal,
  sidePadding,
  expandableElements,
  onExpansionClick
}: {
  groups: Group[];
  renderGroupDivider: (group: Group) => React.ReactNode;
  columns: Column[];
  sortBy: ItemSorter<D, OtherProps>;
  direction: SortDirection;
  otherProps: OtherProps;
  setSort: (
    nextSortBy: ItemSorter<D, OtherProps>,
    nextDirection: SortDirection
  ) => void;
  _onRowClick?: ((d: D, o: OtherProps) => void) | undefined;
  _rowToHrefExternal?: ((d: D, o: OtherProps) => string | null) | undefined;
  sidePadding?: boolean;
  expandableElements?: Map<string, JSX.Element>;
  onExpansionClick?: (rowId: string) => Promise<void>;
}) => {
  return (
    <>
      <RowsRenderer
        variant="contained"
        columns={columns}
        rows={[]}
        renderHead={true}
        sorter={sortBy}
        sortDirection={direction}
        otherProps={otherProps}
        rowToKey={ROW_TO_KEY}
        onHeadClick={(c, dir) => {
          const sorter = SORTERS[c.key];
          if (!sorter) {
            return;
          }
          setSort(sorter, dir || sorter.items.dir);
        }}
        sidePadding={sidePadding}
      />
      {groups.map((group) => {
        if (!group.rows.length) return null;
        return (
          <div key={group.key}>
            <GroupDivider>{renderGroupDivider(group)}</GroupDivider>
            <RowsRenderer
              variant="contained"
              columns={columns}
              rows={group.rows}
              rowToKey={ROW_TO_KEY}
              onRowClick={_onRowClick}
              rowToHrefExternal={_rowToHrefExternal}
              sorter={sortBy}
              sortDirection={direction}
              renderHead={false}
              headProps={{ sticky: true }}
              onHeadClick={(c, dir) => {
                const sorter = SORTERS[c.key];
                if (!sorter) {
                  return;
                }
                setSort(sorter, dir || sorter.items.dir);
              }}
              chunkSize={30}
              rootMargin="300px"
              rowHeight={ROW_HEIGHTS.dense}
              otherProps={otherProps}
              expandableElements={expandableElements}
              sidePadding={sidePadding}
              onExpansionClick={onExpansionClick}
            />
          </div>
        );
      })}
    </>
  );
};

export const Participant = (props: any) => {
  const { participant, ...other } = props;
  return (
    <div {...other}>
      <OnHover>
        <div>{participant}</div>
        <div className="showOnHover">
          <ExternalLink size={14} />
        </div>
      </OnHover>
    </div>
  );
};

const IconContainer = styled('div')((p) => ({
  minWidth: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const ExpandableTask = ({
  subTasks,
  taskTpl
}: {
  subTasks: ISubTask[] | [];
  taskTpl: ITaskTemplate;
}) => {
  const subTaskTemplates = useMemo(() => getSubTaskTemplates(taskTpl), [
    taskTpl
  ]);
  return (
    <SubItemContainer>
      {subTasks.map((s) => {
        const subTaskTemplate = subTaskTemplates.find(
          (task) => task.id === s.templateId
        );
        return (
          <SubTaskItem key={s.id}>
            <SubTaskStatusChip status={s.status} />
            {subTaskTemplate && (
              <FlexContainer justifyContent="flex-start" spacing={0.5}>
                <IconContainer>
                  <SubTaskIcon key={`${s.id}-icon`} s={subTaskTemplate} />
                </IconContainer>
                <SubTaskAsText key={`${s.id}-text`} s={subTaskTemplate} />
              </FlexContainer>
            )}
          </SubTaskItem>
        );
      })}
    </SubItemContainer>
  );
};
